import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTheme } from 'styled-components'
import { AppDispatch, RootState } from 'store'
import { PageFooter, ColorVariant } from 'styling/GlobalStyles'
import { Pagination } from 'components/Pagination/Pagination'
import { useDebounce } from 'hooks/useDebounde'
import { NavBarSearchIcon } from 'assets/icons/NavBarSearchIcon'
import { FilterIcon } from 'assets/icons/FilterIcon'
import { Option, Select } from 'components/Select/Select'
import { AsyncSelect } from 'components/Select/AsyncSelect'
import { baseUrl } from 'utils/axios'
import { AssignmentStatus, IOperationalAssignment } from 'features/assignments/assignments.types'
import { getOperationalAssignments } from 'features/assignments/assignmentsSlice'
import { getSimpleOperationalTeams } from 'features/operationalManagement/operationalManagementSlice'
import { IBasicUserSimple } from 'features/operationalManagement/operationalManagement.types'
import { AssignmentsFiltersRow, FilterIconButton, SearchField } from '../Organogram.styled'
import { OptionsProps, Table } from 'components/Table/Table'
import { Badge } from 'components/Badge/Badge'
import { NormalText } from 'styling/TypographyV2'
import { ORGANOGRAM_PAGE_LIMIT as PAGE_LIMIT } from '../constants'

const getStatusMeta = (
  theme: { colorGreen: string; greyText: string },
  status: AssignmentStatus,
): { label: string; color: string } => {
  switch (status) {
    case AssignmentStatus.Active:
      return { label: 'Active', color: theme.colorGreen }
    case AssignmentStatus.Inactive:
    default:
      return { label: 'Ended', color: theme.greyText }
  }
}

const fmtDate = (iso: string | null | undefined): string => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const yyyy = d.getFullYear()
  return `${dd}/${mm}/${yyyy}`
}

const employeeLabel = (a: IOperationalAssignment): string => {
  if (!a.user) return '—'
  return `${a.user.firstName} ${a.user.lastName}`.trim() || '—'
}

interface AssignmentsTabProps {
  refreshKey?: number
}

export const AssignmentsTab = ({ refreshKey = 0 }: AssignmentsTabProps) => {
  const dispatch = useDispatch<AppDispatch>()
  const theme = useTheme() as any

  const { data, totalPages, isLoading } = useSelector((state: RootState) => state.assignments)
  const { simpleTeams } = useSelector((state: RootState) => state.operationalManagement)

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 400)
  const [employeeOption, setEmployeeOption] = useState<Option | null>(null)
  const [teamId, setTeamId] = useState<number | null>(null)

  const [page, setPage] = useState(1)
  const employeeId = employeeOption?.value == null ? null : (employeeOption.value as number)

  useEffect(() => {
    dispatch(getSimpleOperationalTeams(undefined))
  }, [])

  const filtersRef = useRef({ debouncedSearch, employeeId, teamId })
  const lastRefreshKeyRef = useRef(refreshKey)

  useEffect(() => {
    const prev = filtersRef.current
    const filtersChanged =
      prev.debouncedSearch !== debouncedSearch ||
      prev.employeeId !== employeeId ||
      prev.teamId !== teamId
    const refreshed = lastRefreshKeyRef.current !== refreshKey
    filtersRef.current = { debouncedSearch, employeeId, teamId }
    lastRefreshKeyRef.current = refreshKey

    if ((filtersChanged || refreshed) && page !== 1) {
      setPage(1)
      return
    }
    const q = debouncedSearch.trim()
    dispatch(
      getOperationalAssignments({
        page,
        limit: PAGE_LIMIT,
        ...(q ? { q } : {}),
        ...(employeeId ? { userIds: String(employeeId) } : {}),
        ...(teamId ? { teamIds: String(teamId) } : {}),
      }),
    )
  }, [dispatch, page, debouncedSearch, employeeId, teamId, refreshKey])

  const teamOptions = useMemo<Option[]>(
    () => simpleTeams.map((t) => ({ label: t.name, value: t.id })),
    [simpleTeams],
  )

  const findOption = (opts: Option[], v: number | null): Option | null =>
    v == null ? null : opts.find((o) => o.value === v) ?? null
  const nullEmployee: Option = { label: 'All employees', value: null }
  const nullTeam: Option = { label: 'All teams', value: null }

  const assignmentTableOptions = useMemo<OptionsProps<IOperationalAssignment>[]>(
    () => [
      { header: 'EMPLOYEE', cell: (a) => employeeLabel(a) },
      { header: 'TEAM', cell: (a) => a.operationalTeam?.name ?? '—' },
      { header: 'START', cell: (a) => fmtDate(a.startDate) },
      {
        header: 'END',
        cell: (a) =>
          a.endDate ? (
            fmtDate(a.endDate)
          ) : (
            <NormalText colorVariant={ColorVariant.GREEN}>Open-ended</NormalText>
          ),
      },
      {
        header: 'STATUS',
        cell: (a) => {
          const statusMeta = getStatusMeta(theme, a.status)
          return <Badge text={statusMeta.label} green={a.status === AssignmentStatus.Active} orange={a.status !== AssignmentStatus.Active} />
        },
      },
    ],
    [theme],
  )

  return (
    <>
      <AssignmentsFiltersRow>
        <SearchField>
          <NavBarSearchIcon />
          <input
            type='text'
            aria-label='Search for employee'
            placeholder='Search for employee'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </SearchField>
        <AsyncSelect<IBasicUserSimple>
          placeholder='Employees'
          optionKey='organogram-employees'
          nullValue={nullEmployee}
          value={employeeOption}
          loadOptions={async (q) => {
            const resp = await baseUrl.get<{ data: IBasicUserSimple[] }>('users/basic', {
              params: { page: 1, limit: 20, state: 1, ...(q ? { q } : {}) },
            })
            return resp.data.data
          }}
          decoder={(u) => ({ label: u.fullName, value: u.id })}
          onChange={(opt) => setEmployeeOption(opt.value == null ? null : opt)}
        />
        <Select
          placeholder='All teams'
          options={teamOptions}
          nullValue={nullTeam}
          value={findOption(teamOptions, teamId)}
          onChange={(opt) => setTeamId(opt.value == null ? null : (opt.value as number))}
        />
        <FilterIconButton title='More filters' aria-label='More filters'>
          <FilterIcon />
        </FilterIconButton>
      </AssignmentsFiltersRow>

      <Table<IOperationalAssignment>
        cellKey='id'
        items={data}
        loading={isLoading}
        emptyListOptions={{ title: 'No assignments yet.' }}
        options={assignmentTableOptions}
      />

      {totalPages > 1 && (
        <PageFooter>
          <Pagination pageCount={totalPages} setPage={setPage} forcePage={page - 1} />
        </PageFooter>
      )}
    </>
  )
}
